import React, { useState, useEffect, useMemo } from 'react';
import { useRecordContext, useDataProvider, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Badge,
  Tooltip,
  Fade,
  Collapse,
  IconButton,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  People,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';

import { EntityType } from '../../../../generated/data-contracts';
import { entityConfig } from './constants/entityConfig';
import { useRolePermissionsApi } from './hooks/useRolePermissionsApi';
import { DeletePermissionDialog } from './components/DeletePermissionDialog';
import { EntityPermissionsSection } from './components/EntityPermissionsSection';
import { AddPermissionDialog } from './components/AddPermissionDialog';
import { EditPermissionDialog } from './components/EditPermissionDialog';

export const RolePermissionsManager: React.FC = () => {
  const record = useRecordContext();
  const [permissions, setPermissions] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [permissionToEdit, setPermissionToEdit] = useState<any>(null);
  const [editLoading, setEditLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const { deletePermission, updatePermission } = useRolePermissionsApi();

  // Cargar permisos y usuarios cuando se monta el componente o cambia el rol
  useEffect(() => {
    if (record?.id) {
      setLoading(true);
      dataProvider
        .getOne('role-permissions', { id: record.id })
        .then(response => {
          console.log('🔍 Respuesta completa de la API:', response.data);

          // Extraer los permisos de la propiedad rolePermissions
          const permissionsList = response.data.rolePermissions || [];
          console.log('📋 Permisos extraídos:', permissionsList);
          console.log('📊 Número de permisos:', permissionsList.length);
          setPermissions(Array.isArray(permissionsList) ? permissionsList : []);

          // Extraer los usuarios de la propiedad users
          const usersList = response.data.users || [];
          console.log('👥 Usuarios extraídos:', usersList);
          console.log('🔢 Número de usuarios:', usersList.length);
          setUsers(Array.isArray(usersList) ? usersList : []);
        })
        .catch(error => {
          console.error('❌ Error al cargar datos del rol:', error);
          notify('Error al cargar datos del rol', { type: 'error' });
          setPermissions([]);
          setUsers([]);
        })
        .finally(() => setLoading(false));
    } else {
      console.log('⚠️ No hay record.id disponible:', record);
    }
  }, [record?.id, dataProvider, notify]);

  const handleDeletePermission = (permission: any) => {
    setPermissionToDelete(permission);
    setDeleteDialogOpen(true);
  };

  const handleEditPermission = (permission: any) => {
    setPermissionToEdit(permission);
    setEditDialogOpen(true);
  };

  const confirmEditPermission = async (updatedPermission: any) => {
    if (!updatedPermission) return;

    setEditLoading(true);
    try {
      await updatePermission(updatedPermission);
      notify('Permiso actualizado correctamente', { type: 'success' });

      // Refrescar la lista de permisos desde la API
      if (record?.id) {
        dataProvider
          .getOne('role-permissions', { id: record.id })
          .then(response => {
            const permissionsList = response.data.rolePermissions || [];
            setPermissions(
              Array.isArray(permissionsList) ? permissionsList : []
            );
          })
          .catch(error => {
            console.error('Error al refrescar permisos:', error);
            notify('Error al refrescar permisos', { type: 'error' });
          });
      }

      setEditDialogOpen(false);
      setPermissionToEdit(null);
    } catch (error) {
      console.error('Error updating role permission:', error);
      notify('Error al actualizar permiso', { type: 'error' });
    } finally {
      setEditLoading(false);
    }
  };

  const cancelEditPermission = () => {
    setEditDialogOpen(false);
    setPermissionToEdit(null);
  };

  const confirmDeletePermission = async () => {
    if (!permissionToDelete) return;

    setDeleteLoading(true);
    try {
      await deletePermission(permissionToDelete.id);
      notify('Permiso eliminado correctamente', { type: 'success' });

      // Refrescar la lista de permisos desde la API
      if (record?.id) {
        dataProvider
          .getOne('role-permissions', { id: record.id })
          .then(response => {
            console.log(
              'Permisos actualizados después de eliminación:',
              response.data
            );
            const permissionsList = response.data.rolePermissions || [];
            setPermissions(
              Array.isArray(permissionsList) ? permissionsList : []
            );
            // También actualizar usuarios si es necesario
            const usersList = response.data.users || [];
            setUsers(Array.isArray(usersList) ? usersList : []);
          })
          .catch(error => {
            console.error('Error al refrescar permisos:', error);
            notify('Error al refrescar permisos', { type: 'error' });
          });
      }

      setDeleteDialogOpen(false);
      setPermissionToDelete(null);
    } catch (error) {
      console.error('Error deleting role permission:', error);
      notify('Error al eliminar permiso', { type: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeletePermission = () => {
    setDeleteDialogOpen(false);
    setPermissionToDelete(null);
  };

  const handleAddPermission = (newPermission: any) => {
    // Refrescar la lista de permisos desde la API
    if (record?.id) {
      dataProvider
        .getOne('role-permissions', { id: record.id })
        .then(response => {
          console.log('Permisos actualizados:', response.data);
          const permissionsList = response.data.rolePermissions || [];
          setPermissions(Array.isArray(permissionsList) ? permissionsList : []);
          // También actualizar usuarios
          const usersList = response.data.users || [];
          setUsers(Array.isArray(usersList) ? usersList : []);
        })
        .catch(error => {
          console.error('Error al refrescar permisos:', error);
          notify('Error al refrescar permisos', { type: 'error' });
        });
    }
  };

  if (!record) return null;

  const totalPermissions = permissions.length;
  const totalUsers = users.length;
  console.log('🔄 Estado actual del componente:');
  console.log('- Record ID:', record?.id);
  console.log('- Loading:', loading);
  console.log('- Permissions length:', permissions.length);
  console.log('- Users length:', users.length);
  console.log('- Permissions array:', permissions);
  console.log('- Users array:', users);
  console.log('- Total permissions:', totalPermissions);
  console.log('- Total users:', totalUsers);

  const permissionsByEntity = Object.values(EntityType).map(entityType => ({
    type: entityType,
    count: permissions.filter(p => p.entityType === entityType).length,
    config: entityConfig[entityType],
  }));

  // Componente para mostrar usuarios asignados
  const UsersAssignedSection = () => {
    const [usersExpanded, setUsersExpanded] = useState(false); // Cerrado por defecto

    return (
      <Card
        sx={{
          mb: 3,
          border: '2px solid #4CAF5020',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardHeader
          onClick={() => setUsersExpanded(!usersExpanded)}
          avatar={
            <Avatar
              sx={{
                bgcolor: '#E8F5E8',
                color: '#4CAF50',
                width: 56,
                height: 56,
                boxShadow: '0 4px 12px #4CAF5030',
              }}
            >
              <People sx={{ fontSize: 28 }} />
            </Avatar>
          }
          title={
            <Typography
              variant='h6'
              sx={{ fontWeight: 'bold', color: '#4CAF50' }}
            >
              Usuarios Asignados
            </Typography>
          }
          subheader={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Typography variant='body2' color='textSecondary'>
                Usuarios que tienen este rol asignado
              </Typography>
              <Badge
                badgeContent={totalUsers}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: '#4CAF50',
                    color: 'white',
                    fontWeight: 'bold',
                  },
                }}
              >
                <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </Badge>
            </Box>
          }
          action={
            <IconButton
              onClick={() => setUsersExpanded(!usersExpanded)}
              sx={{
                transform: usersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                color: '#4CAF50',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          }
          sx={{
            bgcolor: '#4CAF5008',
            borderBottom: '2px solid #4CAF5015',
            py: 2,
            cursor: 'pointer',
          }}
        />

        <Collapse in={usersExpanded}>
          <CardContent sx={{ p: 0 }}>
            {users.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: '#4CAF5015',
                    color: '#4CAF50',
                    width: 80,
                    height: 80,
                    margin: '0 auto 16px',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant='h6' color='textSecondary' gutterBottom>
                  No hay usuarios asignados
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  Este rol no tiene usuarios asignados actualmente
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ py: 0 }}>
                {users.map((user, index) => (
                  <Fade in key={user.id} timeout={300 + index * 100}>
                    <ListItem
                      sx={{
                        borderBottom:
                          index < users.length - 1
                            ? '1px solid #4CAF5010'
                            : 'none',
                        py: 2,
                        px: 3,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: '#4CAF5005',
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: '#E8F5E8',
                          color: '#4CAF50',
                          width: 48,
                          height: 48,
                          mr: 2,
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                        }}
                      >
                        {user.fullName
                          ? user.fullName
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .toUpperCase()
                          : user.userName[0].toUpperCase()}
                      </Avatar>

                      <ListItemText
                        primary={
                          <Typography
                            variant='subtitle1'
                            sx={{ fontWeight: 'bold' }}
                          >
                            {user.fullName || user.userName}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <EmailIcon
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                              />
                              <Typography variant='body2' color='textSecondary'>
                                {user.email}
                              </Typography>
                            </Box>
                            {user.phoneNumber && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <PhoneIcon
                                  sx={{ fontSize: 16, color: 'text.secondary' }}
                                />
                                <Typography
                                  variant='body2'
                                  color='textSecondary'
                                >
                                  {user.phoneNumber}
                                </Typography>
                              </Box>
                            )}
                            <Typography variant='caption' color='textSecondary'>
                              Usuario: {user.userName} • ID: {user.id}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  </Fade>
                ))}
              </List>
            )}
          </CardContent>
        </Collapse>
      </Card>
    );
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header Section */}
      <Paper
        sx={{
          p: 1.5,
          mb: 4,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'primary.contrastText',
              width: 44,
              height: 44,
            }}
          >
            <SecurityIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant='h5' sx={{ fontWeight: 'bold', mb: 1 }}>
              Gestión de Permisos
            </Typography>
            <Typography variant='body1' sx={{ opacity: 0.9 }}>
              Configura los permisos específicos que tiene este rol sobre
              diferentes entidades del sistema
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant='h3' sx={{ fontWeight: 'bold' }}>
              {totalPermissions}
            </Typography>
            <Typography variant='body2' sx={{ opacity: 0.8, pr: 0.5 }}>
              Total
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Users Assigned Section */}
      <UsersAssignedSection />

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {permissionsByEntity.map(({ type, count, config }) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={type}>
            <Card
              sx={{
                textAlign: 'center',
                p: 2,
                border: `1px solid ${config.color}30`,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3,
                  bgcolor: config.bgColor,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: config.bgColor,
                  color: config.color,
                  width: 48,
                  height: 48,
                  margin: '0 auto 8px',
                }}
              >
                {config.icon}
              </Avatar>
              <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                {count}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {config.name}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Permissions Sections */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box>
          {Object.values(EntityType).map(entityType => (
            <EntityPermissionsSection
              key={entityType}
              entityType={entityType}
              roleId={record.id}
              permissions={permissions}
              onDelete={handleDeletePermission}
              onEdit={handleEditPermission}
              onAdd={handleAddPermission}
            />
          ))}
        </Box>
      )}

      <DeletePermissionDialog
        open={deleteDialogOpen}
        onClose={cancelDeletePermission}
        onConfirm={confirmDeletePermission}
        permission={permissionToDelete}
        loading={deleteLoading}
      />

      <EditPermissionDialog
        open={editDialogOpen}
        onClose={cancelEditPermission}
        onConfirm={confirmEditPermission}
        permission={permissionToEdit}
        loading={editLoading}
      />
    </Box>
  );
};
